import { ArrowLeft, RotateCw } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'

import { useSesion } from '../api/sesion'
import { esDeAgencia } from '../marca/marca'
import { correoDeAyuda } from './Ayuda'
import { Boton, Tarjeta } from './ui'

/** A quién se escribe cuando algo falla: a Wixy, o a la agencia en su panel (§36). */
export const correoDeContacto = (): string => (esDeAgencia() ? correoDeAyuda() : 'hola@wixy.app')

type Props = { children: ReactNode; esEquipo: boolean; clave: string; pantallaEntera?: boolean }
type Estado = { error: Error | null }

/**
 * Una pantalla que revienta al pintarse se queda en su sitio con un mensaje, en vez de dejar
 * el panel en blanco: el menú sigue ahí, y «Volver atrás» o «Recargar» sacan de ella. El
 * detalle técnico solo lo ve el equipo; al cliente se le dice qué hacer (§28).
 *
 * `clave` es la dirección: al navegar a otra pantalla el error se olvida sin desmontar nada.
 */
class Barrera extends Component<Props, Estado> {
  state: Estado = { error: null }

  static getDerivedStateFromError(error: Error): Estado {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Una pantalla del panel no se ha podido pintar', error, info.componentStack)
  }

  componentDidUpdate(anteriores: Props) {
    if (this.state.error && anteriores.clave !== this.props.clave) this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    const tarjeta = (
      <Tarjeta className="mx-auto max-w-xl">
        <h1 className="font-display text-xl font-semibold tracking-tight">Esta pantalla no se ha podido mostrar</h1>
        <p className="mt-2 text-sm text-tenue">
          Algo ha fallado al pintarla. Lo que tenías guardado sigue guardado. Vuelve atrás o recarga la página; si vuelve a pasar, escríbenos a{' '}
          <a className="text-violeta underline underline-offset-2" href={`mailto:${correoDeContacto()}`}>
            {correoDeContacto()}
          </a>{' '}
          y dinos en qué pantalla estabas.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <Boton icono={<ArrowLeft />} onClick={() => window.history.back()}>
            Volver atrás
          </Boton>
          <Boton icono={<RotateCw />} onClick={() => window.location.reload()} tipo="primario">
            Recargar
          </Boton>
        </div>
        {this.props.esEquipo && (
          <details className="mt-5 text-xs">
            <summary className="cursor-pointer text-tenue">Detalle para el equipo</summary>
            <pre className="mt-2 max-h-72 overflow-auto rounded-campo bg-papel-2 p-3 whitespace-pre-wrap text-tinta-2">{`${error.name}: ${error.message}\n\n${error.stack ?? ''}`}</pre>
          </details>
        )}
      </Tarjeta>
    )
    return this.props.pantallaEntera ? <div className="flex min-h-full items-center justify-center p-4">{tarjeta}</div> : tarjeta
  }
}

/** La barrera, con lo que necesita saber de la sesión (una clase no usa ganchos). */
export const ErrorDePantalla = ({ children, clave, pantallaEntera }: { children: ReactNode; clave: string; pantallaEntera?: boolean }) => {
  const { usuario } = useSesion()
  return (
    <Barrera clave={clave} esEquipo={usuario?.rol === 'superadmin'} pantallaEntera={pantallaEntera}>
      {children}
    </Barrera>
  )
}
